import type { FeedsClient } from '@stream-io/feeds-client'

import { authorizedFetch } from '../../helpers/authorizedFetch'
import trans from '../../helpers/trans'
import { generateApiV3Url } from '../../helpers/url'
import { FetchResult, FetchResultType } from '../FetchResult'
import { NotificationCenterHeaderName, NotificationCenterLog } from '../NotificationCenter/NotificationCenter'
import {
    ValkTalkFeedMessageResource,
    ValkTalkPost,
    ValkTalkPostFormData,
    ValkTalkPostsQuery,
    ValkTalkPostsQueryResult,
} from './ValkTalkPost'
import {
    getCurrentTenant,
    getFeedGroupAndId,
    getValkTalkPostsFeedFilter,
    sortValkTalkPostsByNewest,
    VALK_TALK_POSTS_QUERY_LIMIT,
} from './ValkTalkPostHelpers'
import {
    transformActivityToValkTalkPost,
    transformValkTalkPostFormDataToFeedMessageFormData,
    transformValkTalkPostFormDataToUpdateFeedMessageFormData,
} from './ValkTalkPostTransformers'

// Posts are created, updated and deleted by Valkplanner, which checks the user's permissions and uploads the images:
// users only follow the feeds, which doesn't allow posting. Posts are read straight from Stream with the current
// user's Stream token.

const createErrorResult = (error: unknown, errorKey = 'errors.unknownError'): FetchResult<never, string> => {
    console.error(error)

    return {
        status: 500,
        type: FetchResultType.Error,
        error: trans(errorKey),
    }
}

interface FeedMessageErrorKeys {
    forbidden: string
    fallback: string
}

const getFeedMessageErrorKey = (status: number, errorKeys: FeedMessageErrorKeys): string => {
    switch (status) {
        case 403:
            return errorKeys.forbidden
        case 404:
            return 'entities.valkTalkPost.errors.postNotFound'
        case 409:
            return 'entities.valkTalkPost.errors.noStreamAccess'
        case 502:
            return 'entities.valkTalkPost.errors.streamConnectionFailed'
        default:
            return errorKeys.fallback
    }
}

const createFeedMessageErrorResult = (status: number, errorKeys: FeedMessageErrorKeys): FetchResult<never, string> => ({
    status,
    type: FetchResultType.Error,
    error: trans(getFeedMessageErrorKey(status, errorKeys)),
})

const transformFeedMessageResponseToValkTalkPost = async (response: Response): Promise<ValkTalkPost> => {
    const { activity }: ValkTalkFeedMessageResource = await response.json()
    const post = transformActivityToValkTalkPost(activity)

    if (! post) {
        throw new Error(`Activity ${activity.id} is not in a ValkTalk feed`)
    }

    return post
}

const createPostErrorKeys: FeedMessageErrorKeys = {
    forbidden: 'entities.valkTalkPost.errors.postForbidden',
    fallback: 'entities.valkTalkPost.errors.createPostFailed',
}

export const postValkTalkPostApiCall = async (
    formData: ValkTalkPostFormData,
    eventLogTrackerData: NotificationCenterLog,
): Promise<FetchResult<ValkTalkPost, string>> => {
    try {
        const url = generateApiV3Url({ endpoint: '/stream/feed-messages' })

        const response = await authorizedFetch(url, {
            method: 'POST',
            body: transformValkTalkPostFormDataToFeedMessageFormData(formData),
            headers: {
                [NotificationCenterHeaderName.X_EVENT]: JSON.stringify(eventLogTrackerData),
            },
            // Lets the browser set the multipart content type, including its boundary
            disableContentType: true,
        })

        if (! response.ok) {
            return createFeedMessageErrorResult(response.status, createPostErrorKeys)
        }

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: await transformFeedMessageResponseToValkTalkPost(response),
        }
    } catch (error: unknown) {
        return createErrorResult(error, createPostErrorKeys.fallback)
    }
}

const updatePostErrorKeys: FeedMessageErrorKeys = {
    forbidden: 'entities.valkTalkPost.errors.updatePostForbidden',
    fallback: 'entities.valkTalkPost.errors.updatePostFailed',
}

// A POST, as Valkplanner (PHP) only parses multipart bodies on POST requests
export const patchValkTalkPostApiCall = async (
    id: string,
    formData: ValkTalkPostFormData,
    eventLogTrackerData: NotificationCenterLog,
): Promise<FetchResult<ValkTalkPost, string>> => {
    try {
        const url = generateApiV3Url({ endpoint: `/stream/feed-messages/${encodeURIComponent(id)}` })

        const response = await authorizedFetch(url, {
            method: 'POST',
            body: transformValkTalkPostFormDataToUpdateFeedMessageFormData(formData),
            headers: {
                [NotificationCenterHeaderName.X_EVENT]: JSON.stringify(eventLogTrackerData),
            },
            // Lets the browser set the multipart content type, including its boundary
            disableContentType: true,
        })

        if (! response.ok) {
            return createFeedMessageErrorResult(response.status, updatePostErrorKeys)
        }

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: await transformFeedMessageResponseToValkTalkPost(response),
        }
    } catch (error: unknown) {
        return createErrorResult(error, updatePostErrorKeys.fallback)
    }
}

export const getValkTalkPostsApiCall = async (
    client: FeedsClient,
    query: ValkTalkPostsQuery,
): Promise<FetchResult<ValkTalkPostsQueryResult, string>> => {
    try {
        const filter = getValkTalkPostsFeedFilter(query.month, query.authorId)

        const responses = await Promise.all(query.feedTypes.map(feedType => {
            const [feedGroupId, feedId] = getFeedGroupAndId(feedType, getCurrentTenant())

            return client.getOrCreateFeed({
                feed_group_id: feedGroupId,
                feed_id: feedId,
                filter,
                limit: VALK_TALK_POSTS_QUERY_LIMIT,
                watch: false,
            })
        }))

        const posts = responses
            .flatMap(response => response.activities)
            .map(transformActivityToValkTalkPost)
            .filter((post): post is ValkTalkPost => !! post)

        return {
            status: 200,
            type: FetchResultType.Success,
            data: {
                posts: sortValkTalkPostsByNewest(posts),
                hasMorePosts: responses.some(response => !! response.next),
            },
        }
    } catch (error: unknown) {
        return createErrorResult(error, 'entities.valkTalkPost.errors.getPostsFailed')
    }
}

const deletePostErrorKeys: FeedMessageErrorKeys = {
    forbidden: 'entities.valkTalkPost.errors.deletePostForbidden',
    fallback: 'entities.valkTalkPost.errors.deletePostFailed',
}

// Valkplanner hard deletes the activity, so its comments are removed too
export const deleteValkTalkPostApiCall = async (
    id: string,
    eventLogTrackerData: NotificationCenterLog,
): Promise<FetchResult<undefined, string>> => {
    try {
        const url = generateApiV3Url({ endpoint: `/stream/feed-messages/${encodeURIComponent(id)}` })

        const response = await authorizedFetch(url, {
            method: 'DELETE',
            headers: {
                [NotificationCenterHeaderName.X_EVENT]: JSON.stringify(eventLogTrackerData),
            },
        })

        if (! response.ok) {
            return createFeedMessageErrorResult(response.status, deletePostErrorKeys)
        }

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: undefined,
        }
    } catch (error: unknown) {
        return createErrorResult(error, deletePostErrorKeys.fallback)
    }
}
